<script lang="ts">
  import { goto } from "$app/navigation";

  import SvelteMarkdown from "svelte-markdown";

  import { themeStore } from "$lib/gpu-glossary";
  import type { Page } from "$lib/gpu-glossary";
  import GlobalHotKey from "$lib/ui/common/GlobalHotKey.svelte";
  import GlossaryEm from "$lib/ui/gpu-glossary/GlossaryEm.svelte";
  import GlossaryImage from "$lib/ui/gpu-glossary/GlossaryImage.svelte";
  import GlossaryLink from "$lib/ui/gpu-glossary/GlossaryLink.svelte";
  import GlossaryList from "$lib/ui/gpu-glossary/GlossaryList.svelte";
  import SectionContents from "$lib/ui/gpu-glossary/SectionContents.svelte";
  import type { PageData } from "./$types";

  export let data: PageData;

  let showFeedbackBox = false;

  const toggleFeedbackBox = () => {
    showFeedbackBox = !showFeedbackBox;
  };

  const goToPage = (page: Page | null) => {
    if (page) {
      goto(page.href);
    }
  };

  const hotKeys = [
    { hotKey: "ArrowLeft", action: () => goToPage(data.prevPage) },
    { hotKey: "ArrowRight", action: () => goToPage(data.nextPage) },
    { hotKey: "ArrowUp", action: () => goToPage(data.prevPage) },
    { hotKey: "ArrowDown", action: () => goToPage(data.nextPage) },
  ];
</script>

<GlobalHotKey
  hotKey="?"
  shift
  ctrlOrCmd={false}
  on:hotkey={toggleFeedbackBox}
/>

{#each hotKeys as { hotKey, action }}
  <GlobalHotKey {hotKey} ctrlOrCmd={false} on:hotkey={action} />
{/each}
<div
  class="sm:px-14 px-6 sm:pt-8 pt-4 flex flex-col justify-between h-full"
  style:background-color={$themeStore.secondary[100]}
>
  <div class="flex flex-col">
    <div
      class="glossary-font-secondary mb-3"
      style:color={$themeStore.primary[60]}
    >
      {data.metadata.path}
    </div>
    <div class="flex items-center gap-2 pb-4">
      <h1 class="text-3xl font-medium">{data.metadata.title}</h1>
      {#if data.metadata.abbreviation}
        <div
          class="text-2xl rounded-sm px-2 h-fit"
          style:color={$themeStore.primary[80]}
          style:background-color={$themeStore.primary[5]}
        >
          {data.metadata.abbreviation}
        </div>
      {/if}
    </div>
    <div class="flex flex-col gap-y-3" style:color={$themeStore.primary[80]}>
      <SvelteMarkdown
        renderers={{
          link: GlossaryLink,
          em: GlossaryEm,
          image: GlossaryImage,
          list: GlossaryList,
        }}
        source={data.content}
      />
    </div>
    {#if data.isTopLevel && data.children}
      <SectionContents contents={data.children} />
    {/if}
  </div>
  <div
    class="flex justify-between mt-6 text-sm sm:text-base pb-8 items-end sm:static sticky bottom-0 z-5 pt-2 sm:pt-0"
    style:background-color={$themeStore.secondary[100]}
  >
    {#if data.prevPage}
      <a
        href={data.prevPage.href}
        class="flex items-center gap-2 max-w-[50%] self-center"
        ><svg
          width="20"
          height="16"
          viewBox="0 0 20 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.75 13H8.6667V10.8334L6.5 10.8334V8.66672L19.5 8.66672V6.50002L6.5 6.50002V4.33332H8.6667V2.16672H9.75V1.71661e-05H6.5V2.16672H4.3333V4.33332H2.1667V5.41672H1.0833V6.50002H0V8.66672H1.0833V9.75002L2.1667 9.75002V10.8334H4.3333V13H6.5V15.1667H9.75V13Z"
            fill="currentColor"
          />
        </svg>
        <span class="hidden sm:block">{data.prevPage.title}</span></a
      >
    {:else}
      <div></div>
    {/if}
    <div class="flex flex-col items-end gap-4 relative">
      <div
        class="border p-2.5 leading-[18px] absolute bottom-[32px] right-0 z-[10] whitespace-nowrap fade-blur"
        style:border-color={$themeStore.primary[100]}
        style:background-color={$themeStore.secondary[100]}
        class:visible={showFeedbackBox}
      >
        Something seem wrong?<br />Or want to contribute?<br /><br />Click this
        button to<br />let us know on GitHub.
      </div>
      <div class="flex items-center gap-4">
        {#if data.nextPage}
          <a href={data.nextPage.href} class="flex items-center gap-2"
            ><span class="hidden sm:block">{data.nextPage.title}</span><svg
              width="20"
              height="16"
              viewBox="0 0 20 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.75 2.16666H10.8333V4.33333H13V6.49997H0L0 8.66667H13V10.8334H10.8333V13H9.75V15.1667H13V13H15.1667V10.8334H17.3333V9.74997H18.4167V8.66667H19.5V6.49997H18.4167V5.41667H17.3333V4.33333H15.1667V2.16666H13V0L9.75 0V2.16666Z"
                fill="currentColor"
              />
            </svg>
          </a>
        {/if}
        <a
          href="https://github.com/modal-labs/gpu-glossary/issues/new"
          target="_blank"
          rel="noopener noreferrer"
          class="rounded-full w-8 h-8 flex items-center justify-center glossary-font-secondary border flex-shrink-0"
          style:background-color={$themeStore.primary[20]}
          style:border-color={$themeStore.primary[100]}
          on:mouseenter={() => (showFeedbackBox = true)}
          on:mouseleave={() => (showFeedbackBox = false)}
        >
          ?
        </a>
      </div>
    </div>
  </div>
</div>

<style>
  .fade-blur {
    opacity: 0;
    filter: blur(10px);
    transition:
      opacity 0.5s ease,
      filter 0.5s ease,
      transform 0.5s ease;
  }

  .fade-blur.visible {
    opacity: 1;
    filter: blur(0);
    transform: translateY(-14px);
  }
</style>
